import { forwardRef, ReactElement, Ref, useMemo } from 'react'
import cn from 'utils/cn'
import { HTMLMotionProps, motion } from 'framer-motion'
import Icon from 'core/components/icons/Icon'
import { IconType } from 'core/components/icons/types'
import 'core/components/buttons/Button.styles.scss'

export interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'prefix'> {
  /**
   * Defines the button’s visual style (default, primary, error, outline, link).
   */
  variant?: 'default' | 'primary' | 'error' | 'outline' | 'link'

  /**
   * Element displayed before the content, commonly used for an icon.
   */
  prefix?: ReactElement

  /**
   * Element displayed after the content, such as an icon or badge.
   */
  suffix?: ReactElement

  /**
   * Disables the button, preventing clicks and applying a disabled style.
   */
  disabled?: boolean

  /**
   * Shows an async state (loading, success, error, idle).
   */
  status?: 'loading' | 'pending' | 'success' | 'error' | 'idle'

  /**
   * Shorthand for rendering an icon before the button content.
   */
  icon?: `${IconType}`

  /** Custom CSS classes for styling overrides. */
  className?: string

  /** Native button type (`button`, `submit`, `reset`). */
  type?: 'button' | 'submit' | 'reset'
}

/**
 * @description: Basic button for user interaction.
 */
const Button = forwardRef(
  (
    {
      className = '',
      children,
      variant = 'default',
      prefix,
      suffix,
      type = 'button',
      disabled,
      onClick,
      status,
      icon,
      ...props
    }: ButtonProps,
    ref: Ref<HTMLButtonElement>
  ) => {
    const variantStyles: Record<string, string> = useMemo(
      () => ({
        default: 'button-default',
        primary: 'button-primary',
        error: 'button-error',
        outline: 'button-outline',
        link: 'button-link'
      }),
      []
    )

    const iconStyles: Record<string, string> = useMemo(
      () => ({
        primary: '[&>svg]:text-white [&>div>svg]:text-white',
        outline:
          'group-hover:[&>svg]:text-primary group-hover:[&>div>svg]:text-primary'
      }),
      []
    )

    const isIconButton = !children && (prefix || suffix)

    const asyncPrefixIcon = useMemo(() => {
      if ((status === 'idle' && icon) || (!children && icon)) {
        return <Icon name={icon} size={14} className="mb-px" />
      }
      if (status === 'loading' || status === 'pending') {
        return <Icon name="loading" size={14} className="mb-0.75" />
      }
      if (status === 'success') {
        return <Icon name="check-circle-outline" size={14} className="mb-0.5" />
      }
      if (status === 'error') {
        return <Icon name="close-circle-outline" size={14} className="mb-0.5" />
      }

      return null
    }, [status, icon, children])

    return (
      <motion.button
        className={cn([
          'inline-flex items-center justify-center whitespace-nowrap group font-medium rounded-md outline-1! transition-colors duration-300 group cursor-pointer',
          'disabled:pointer-events-none disabled:cursor-not-allowed disabled:text-dark-7! disabled:hover:shadow-none',
          variantStyles[variant],
          isIconButton
            ? 'px-2.5 py-2'
            : variant === 'link'
              ? 'p-0'
              : 'px-4 py-2',
          className,
          'disabled:pointer-events-none disabled:cursor-not-allowed disabled:text-dark-7! disabled:hover:shadow-none'
        ])}
        type={type}
        disabled={disabled}
        ref={ref}
        onClick={(e) => {
          e.currentTarget.blur()
          if (status === 'loading' || status === 'pending') return
          onClick?.(e)
        }}
        {...props}
      >
        <>
          {prefix && !asyncPrefixIcon && (
            <span className={cn(children && 'mr-2', iconStyles[variant] ?? '')}>
              {prefix}
            </span>
          )}
          {asyncPrefixIcon && (
            <span className={cn(children && 'mr-2', iconStyles[variant] ?? '')}>
              {asyncPrefixIcon}
            </span>
          )}
          {children}
          {suffix && (
            <span className={cn(children && 'ml-2', iconStyles[variant] ?? '')}>
              {suffix}
            </span>
          )}
        </>
      </motion.button>
    )
  }
)

export default Button
